import { inject, Injectable } from '@angular/core';
import { ApplicationStore } from 'app/state-management/application/application.store';
import { AssetNodeType, WebPubSubRequest, WebPubSubResponse, WebPubSubService } from '@festo-ax/smartenance-shared-ui';
import { from, Observable } from 'rxjs';
import { Identity } from '../models/api/identity';
import { FALLBACK_LANGUAGE, MAJOR_TIMEOUT, MINOR_TIMEOUT } from '../tokens';
import { UserSettings } from '../models/api/user-settings';
import { SapUtils } from '../utils/reducers/sap.utils';
import { SapHierarchyPathResponse, SapHierarchyResponse } from '../models/api/sap-asset';

@Injectable({
  providedIn: 'root'
})
export class SapAssetsService {
  private webPubSubService: WebPubSubService = inject(WebPubSubService);
  private appStore: ApplicationStore = inject(ApplicationStore);

  private readonly majorTimeout: number = inject(MAJOR_TIMEOUT);
  private readonly minorTimeout: number = inject(MINOR_TIMEOUT);
  private readonly sapLanguageFallback: string = inject(FALLBACK_LANGUAGE);

  getGroupHierarchy(
    nodeId: string | null,
    identity: Identity,
    userSettings: UserSettings,
    minorTimeout: boolean = false
  ): Observable<WebPubSubResponse<'getFunctionalLocationAssetHierarchy'>> {
    const baseRequest:  WebPubSubRequest<'getFunctionalLocationAssetHierarchy'> = {
      topic: 'getFunctionalLocationAssetHierarchy',
      data: { filterOptions: { depthLevel: 1, ...(nodeId ? { id: nodeId } : {}) } },
    };

    const request: WebPubSubRequest<'getFunctionalLocationAssetHierarchy'> = SapUtils
      .mergeRequestWithLanguageFromUserSettings(baseRequest, userSettings, this.sapLanguageFallback);

    return from(
      this.webPubSubService.sendRequest(
        request,
        identity,
        { timeout: minorTimeout ? this.minorTimeout : this.majorTimeout }
      )
    );
  }

  getMachineHierarchy(
    nodeId: string,
    identity: Identity,
    userSettings: UserSettings,
    minorTimeout: boolean = false
  ): Observable<WebPubSubResponse<'getEquipmentAssetHierarchy'>> {
    const baseRequest:  WebPubSubRequest<'getEquipmentAssetHierarchy'> = {
      topic: 'getEquipmentAssetHierarchy',
      data: { filterOptions: { id: nodeId, depthLevel: 1 } },
    };

    const request: WebPubSubRequest<'getEquipmentAssetHierarchy'> = SapUtils
      .mergeRequestWithLanguageFromUserSettings(baseRequest, userSettings, this.sapLanguageFallback);

    return from(
      this.webPubSubService.sendRequest(
        request,
        identity,
        { timeout: minorTimeout ? this.minorTimeout : this.majorTimeout }
      )
    );
  }

  getSapAssetTreePartByNodeId(nodeId: string, nodeType: AssetNodeType, identity: Identity, userSettings: UserSettings): Observable<SapHierarchyResponse> {
    if (nodeType === AssetNodeType.GROUP) {
      return this.getGroupHierarchy(nodeId, identity, userSettings, true);
    }

    return this.getMachineHierarchy(nodeId, identity, userSettings, true);
  }

  getGroupPath(
    nodeId: string,
    identity: Identity,
    userSettings: UserSettings
  ): Observable<WebPubSubResponse<'getAssetHierarchyPathOfFunctionalLocation'>> {
    const baseRequest:  WebPubSubRequest<'getAssetHierarchyPathOfFunctionalLocation'> = {
      topic: 'getAssetHierarchyPathOfFunctionalLocation',
      data: { id: nodeId },
    };

    const request: WebPubSubRequest<'getAssetHierarchyPathOfFunctionalLocation'> = SapUtils
      .mergeRequestWithLanguageFromUserSettings(baseRequest, userSettings, this.sapLanguageFallback);

    return from(
      this.webPubSubService.sendRequest(
        request,
        identity,
        { timeout: this.majorTimeout }
      )
    );
  }

  getMachinePath(
    nodeId: string,
    identity: Identity,
    userSettings: UserSettings
  ): Observable<WebPubSubResponse<'getAssetHierarchyPathOfEquipment'>> {
    const baseRequest:  WebPubSubRequest<'getAssetHierarchyPathOfEquipment'> = {
      topic: 'getAssetHierarchyPathOfEquipment',
      data: { id: nodeId },
    };

    const request: WebPubSubRequest<'getAssetHierarchyPathOfEquipment'> = SapUtils
      .mergeRequestWithLanguageFromUserSettings(baseRequest, userSettings, this.sapLanguageFallback);

    return from(
      this.webPubSubService.sendRequest(
        request,
        identity,
        { timeout: this.majorTimeout }
      )
    );
  }

  getSapAssetTreePathByNodeId(nodeId: string, nodeType: AssetNodeType, identity: Identity, userSettings: UserSettings): Observable<SapHierarchyPathResponse> {
    if (nodeType === AssetNodeType.GROUP) {
      return this.getGroupPath(nodeId, identity, userSettings);
    }

    return this.getMachinePath(nodeId, identity, userSettings);
  }
}
